import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useClientAuth } from '../../context/ClientAuthContext';
import { FreeTrialSignupCard } from '../../components/FreeTrialSignupCard';
import { StickyTrialBanner } from '../../components/StickyTrialBanner';
import AICoachGuestChat from '../../components/AICoachGuestChat';
import {
  Flame, Beef, Wheat, Droplet, Calendar, TrendingDown, TrendingUp,
  Target, Sparkles, Download, Check, ArrowLeft, Award,
  Share2, Copy, MessageCircle, Facebook, LayoutDashboard
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { SEOHead } from '../../hooks/usePageSEO';
import { printPlan } from './printPlan';
import PaidContent from './PaidContent';
import ProgramBuildingLoader from './ProgramBuildingLoader';
import { MealPlanCalendar } from '../tracking/MealPlanCalendar';

/* Animated count-up number */
const CountUp = ({ value, decimals = 0, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    let start = null;
    const from = 0;
    const to = Number(value) || 0;
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);
  return <>{display.toFixed(decimals)}</>;
};

const StatCard = ({ icon: Icon, value, label, color, decimals = 0, suffix = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="relative overflow-hidden bg-gradient-to-br from-zinc-900/90 to-black/90 backdrop-blur-xl border border-white/10 shadow-[0_0_35px_-10px_rgba(217,70,239,0.3)] rounded-2xl p-5 sm:p-6"
    data-testid={`stat-card-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
  >
    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: color }} />
    <Icon className="w-7 h-7 mb-3" style={{ color }} />
    <div className="text-3xl sm:text-4xl font-extrabold text-white tabular-nums">
      <CountUp value={value} decimals={decimals} />{suffix}
    </div>
    <p className="text-sm text-zinc-400 mt-1">{label}</p>
  </motion.div>
);

const MACRO_COLORS = { protein: '#E91E63', carbs: '#FBBF24', fat: '#10B981' };

// Forme active agrandie pour le graphique interactif
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 8} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      <Sector cx={cx} cy={cy} innerRadius={outerRadius + 12} outerRadius={outerRadius + 16} startAngle={startAngle} endAngle={endAngle} fill={fill} opacity={0.5} />
    </g>
  );
};

/* 12-week weight projection — "future self" motivator */
const WeightProjection = ({ startWeight, weeklyChange, targetWeight, goal, language }) => {
  if (!startWeight || !weeklyChange || Math.abs(weeklyChange) < 0.02) return null;
  const en = language === 'en';
  const gaining = weeklyChange > 0;
  const color = gaining ? '#facc15' : '#ec4899';
  const data = [];
  for (let w = 0; w <= 12; w++) {
    let val = startWeight + weeklyChange * w;
    if (targetWeight) val = gaining ? Math.min(val, targetWeight) : Math.max(val, targetWeight);
    data.push({ week: w, label: `S${w}`, weight: Math.round(val * 10) / 10 });
  }
  const finalW = data[data.length - 1].weight;
  const diff = Math.round((finalW - startWeight) * 10) / 10;
  const vals = data.map((d) => d.weight);
  const yMin = Math.floor(Math.min(...vals) - 1);
  const yMax = Math.ceil(Math.max(...vals) + 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.5 }}
      className="bg-gradient-to-br from-zinc-900/90 to-black/90 backdrop-blur-xl border border-white/10 shadow-[0_0_35px_-10px_rgba(217,70,239,0.3)] rounded-2xl p-6 mb-10"
      data-testid="weight-projection"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            {gaining ? <TrendingUp className="w-5 h-5 text-yellow-400" /> : <TrendingDown className="w-5 h-5 text-primary" />}
            {en ? 'Your 12-week projection' : 'Votre projection sur 12 semaines'}
          </h3>
          <p className="text-zinc-400 text-sm mt-0.5">
            {en ? 'If you stay consistent with this plan' : 'Si vous restez régulier sur ce plan'}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black tabular-nums" style={{ color }}>{finalW} kg</div>
          <div className="text-xs font-semibold" style={{ color }}>
            {diff > 0 ? '+' : ''}{diff} kg {en ? 'in 12 weeks' : 'en 12 semaines'}
          </div>
        </div>
      </div>
      <div className="h-56" data-testid="weight-projection-chart">
        <ResponsiveContainer width="100%" height="100%" minHeight={224}>
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={{ stroke: '#ffffff15' }} tickLine={false} interval={1} />
            <YAxis domain={[yMin, yMax]} tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={false} tickLine={false} width={44} unit=" kg" />
            <Tooltip
              contentStyle={{ background: '#18181b', border: '1px solid #ffffff20', borderRadius: 12, color: '#fff' }}
              labelStyle={{ color: '#a1a1aa' }}
              formatter={(v) => [`${v} kg`, en ? 'Weight' : 'Poids']}
              labelFormatter={(l) => (en ? `Week ${String(l).replace('S', '')}` : `Semaine ${String(l).replace('S', '')}`)}
            />
            <Area type="monotone" dataKey="weight" stroke={color} strokeWidth={3} fill="url(#projGrad)" isAnimationActive animationDuration={1100} dot={false} activeDot={{ r: 5, fill: color }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-zinc-500 mt-3 text-center">
        {en
          ? 'Estimate based on your target calories. A Body-Up coach fine-tunes it week after week to keep you on track.'
          : "Estimation basée sur vos calories cibles. Un coach Body-Up l'ajuste semaine après semaine pour vous garder sur la bonne voie."}
      </p>
    </motion.div>
  );
};


const ResultsView = ({
  results, formData, calculationId, proContent, mealPlanContent, mealPlanStructured,
  onUpgrade, onRedeemCode, isLoadingPro, genStep, discountMode, onReset, language
}) => {
  const macros = results.macros || {};
  const timeline = results.timeline || {};
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [activeMacro, setActiveMacro] = useState(0);
  const [mealPlanView, setMealPlanView] = useState('calendar'); // 'calendar' | 'text'
  const { user: connectedUser } = useClientAuth();
  const planUnlocked = !!mealPlanContent;
  const planGenerating = isLoadingPro || promoLoading;
  const hasCalendar = !!(mealPlanStructured && mealPlanStructured.days && mealPlanStructured.days.length > 0);

  const applyPromo = async () => {
    if (!promoCode.trim() || !onRedeemCode) return;
    setPromoError('');
    setPromoLoading(true);
    // Le code gratuit débloque le pack complet (Pro + Plan Alimentaire)
    const res = await onRedeemCode('meal_plan', promoCode.trim());
    setPromoLoading(false);
    if (!res.ok) setPromoError(res.message || (language === 'en' ? 'Invalid code' : 'Code invalide'));
  };

  const macroData = [
    { name: language === 'en' ? 'Protein' : 'Protéines', value: macros.protein_cal, grams: macros.protein_g, color: MACRO_COLORS.protein },
    { name: language === 'en' ? 'Carbs' : 'Glucides', value: macros.carbs_cal, grams: macros.carbs_g, color: MACRO_COLORS.carbs },
    { name: language === 'en' ? 'Fat' : 'Lipides', value: macros.fat_cal, grams: macros.fat_g, color: MACRO_COLORS.fat },
  ];
  const macroTotal = macroData.reduce((s, m) => s + (m.value || 0), 0) || 1;
  macroData.forEach((m) => { m.pct = Math.round((m.value / macroTotal) * 100); });
  const active = macroData[activeMacro] || macroData[0];

  const goalLabel = {
    weight_loss: language === 'en' ? 'Fat loss' : 'Perte de gras',
    muscle_gain: language === 'en' ? 'Muscle gain' : 'Prise de muscle',
    maintenance: language === 'en' ? 'Maintenance' : 'Maintien',
    recomposition: language === 'en' ? 'Body recomp' : 'Recomposition corporelle',
  }[formData.goal];

  const handlePrint = (title, markdownContent, isMeal) => printPlan(title, markdownContent, isMeal);

  return (
    <>
      <SEOHead
        title="Vos Résultats Caloriques | Body-Up Nyon"
        description="Vos besoins caloriques personnalisés calculés scientifiquement."
        path="/calculateur-calories"
      />
      <StickyTrialBanner language={language} />
      <main className="min-h-screen bg-black pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
              <button
                onClick={onReset}
                className="flex items-center gap-2 text-zinc-400 hover:text-primary transition-colors text-sm"
                data-testid="results-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> {language === 'en' ? 'New calculation' : 'Nouveau calcul'}
              </button>
              {connectedUser && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="results-client-ctas">
                  <Link
                    to="/mon-espace"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] transition-transform"
                    data-testid="results-back-to-space"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {language === 'en' ? 'Back to my space' : 'Retour à mon espace'}
                  </Link>
                  {planUnlocked && (
                    <a
                      href="#meal-plan-section"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-yellow-300 border border-yellow-400/40 bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors"
                      data-testid="results-view-plan"
                    >
                      <Sparkles className="w-4 h-4" />
                      {language === 'en' ? 'View my plan' : 'Voir mon plan'}
                    </a>
                  )}
                  {planGenerating && !planUnlocked && (
                    <span
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-zinc-300 border border-white/15 bg-zinc-900/60"
                      data-testid="results-plan-generating"
                    >
                      <Sparkles className="w-4 h-4 animate-pulse text-yellow-400" />
                      {language === 'en' ? 'Plan being generated…' : 'Plan en cours de génération…'}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-primary/20 to-yellow-500/20 rounded-full border border-primary/30 mb-4">
              <Award className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-bold text-white tracking-wider">
                {language === 'en' ? 'YOUR PERSONALIZED RESULTS' : 'VOS RÉSULTATS PERSONNALISÉS'}
              </span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white">
              {language === 'en' ? 'Your daily target' : 'Votre objectif quotidien'}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-yellow-400">
                {goalLabel}
              </span>
            </h1>
          </motion.div>

          {/* Avertissements de sécurité / cohérence (transparence & crédibilité) */}
          {Array.isArray(results.warnings) && results.warnings.length > 0 && (() => {
            const en = language === 'en';
            const NOTICES = {
              calorie_floor: en
                ? "For your safety, we've capped the deficit: going lower than this is not recommended without medical supervision."
                : "Pour votre sécurité, nous avons limité le déficit : descendre plus bas n'est pas recommandé sans suivi médical.",
              minor_safety: en
                ? "You're under 18: we only suggest a gentle, growth-friendly approach. Please involve a parent and a health professional."
                : "Vous avez moins de 18 ans : nous ne proposons qu'une approche douce, compatible avec la croissance. Impliquez un parent et un professionnel de santé.",
              training_capped: en
                ? "Your reported training volume is very high — we've capped its calorie contribution to stay realistic."
                : "Votre volume d'entraînement déclaré est très élevé — nous avons plafonné sa contribution calorique pour rester réaliste.",
              target_incoherent: en
                ? "Your target weight doesn't match your goal, so we ignored it. Adjust it for a precise timeline."
                : "Votre poids cible ne correspond pas à votre objectif : nous l'avons ignoré. Ajustez-le pour une estimation précise.",
            };
            const shown = results.warnings.filter((w) => NOTICES[w]);
            if (shown.length === 0) return null;
            return (
              <div className="mb-8 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 space-y-2" data-testid="safety-notices">
                {shown.map((w) => (
                  <p key={w} className="text-amber-200 text-sm flex items-start gap-2">
                    <Award className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
                    <span>{NOTICES[w]}</span>
                  </p>
                ))}
              </div>
            );
          })()}

          {/* Objectifs personnalisés (override Suivi Pro) — garantit la cohérence avec le tableau de bord */}
          {results.goals_overridden && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10"
              data-testid="goals-overridden-badge"
            >
              <Target className="w-4 h-4 text-emerald-400 shrink-0" />
              <p className="text-sm text-emerald-200">
                {language === 'en'
                  ? 'Your personalized targets from your dashboard are applied here.'
                  : 'Vos objectifs personnalisés définis dans votre tableau de bord sont appliqués ici.'}
              </p>
            </motion.div>
          )}

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={Flame} value={results.target_calories} label={language === 'en' ? 'Calories / day' : 'Calories / jour'} color="#E91E63" delay={0.05} />
            <StatCard icon={Beef} value={macros.protein_g} label={language === 'en' ? 'Protein (g)' : 'Protéines (g)'} color="#EC4899" delay={0.12} />
            <StatCard icon={Wheat} value={macros.carbs_g} label={language === 'en' ? 'Carbs (g)' : 'Glucides (g)'} color="#FBBF24" delay={0.19} />
            <StatCard icon={Droplet} value={macros.fat_g} label={language === 'en' ? 'Fat (g)' : 'Lipides (g)'} color="#10B981" delay={0.26} />
          </div>

          {/* Carte de conversion #1 : compte gratuit + essai 7 jours — affichée à TOUS les
              leads (payants ou non), juste après les 4 chiffres clés pour une visibilité
              maximale, avant les graphiques détaillés et tout paywall */}
          <FreeTrialSignupCard email={formData.email} language={language} />

          {/* Chart + breakdown */}
          <div className="grid lg:grid-cols-2 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-zinc-900/90 to-black/90 backdrop-blur-xl border border-white/10 shadow-[0_0_35px_-10px_rgba(217,70,239,0.3)] rounded-2xl p-6"
            >
              <h3 className="text-lg font-bold text-white mb-2">{language === 'en' ? 'Macro distribution' : 'Répartition des macros'}</h3>
              <div className="relative h-64" data-testid="macro-pie-chart">
                <ResponsiveContainer width="100%" height="100%" minHeight={256}>
                  <PieChart>
                    <Pie
                      data={macroData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={66} outerRadius={92} paddingAngle={3}
                      activeIndex={activeMacro} activeShape={renderActiveShape}
                      onMouseEnter={(_, idx) => setActiveMacro(idx)}
                      isAnimationActive animationDuration={900}
                    >
                      {macroData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} stroke="none" style={{ cursor: 'pointer', outline: 'none' }} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {/* Center overlay (interactive) */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <motion.div
                    key={activeMacro}
                    initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                    className="text-center"
                  >
                    <div className="calc-display text-4xl font-black tabular-nums" style={{ color: active.color }}>{active.pct}%</div>
                    <div className="text-sm font-semibold text-white mt-0.5">{active.name}</div>
                    <div className="text-xs text-zinc-500">{active.grams} g · {Math.round(active.value)} kcal</div>
                  </motion.div>
                </div>
              </div>
              {/* Interactive legend with percentages */}
              <div className="grid grid-cols-3 gap-2 mt-3">
                {macroData.map((m, i) => (
                  <button
                    key={m.name}
                    type="button"
                    onMouseEnter={() => setActiveMacro(i)}
                    onClick={() => setActiveMacro(i)}
                    className={`flex flex-col items-center gap-1 rounded-xl py-2 px-1 border transition-all ${activeMacro === i ? 'border-white/30 bg-white/5 scale-[1.03]' : 'border-transparent opacity-70 hover:opacity-100'}`}
                    data-testid={`macro-legend-${i}`}
                  >
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
                      <span className="text-xs text-zinc-300">{m.name}</span>
                    </span>
                    <span className="text-sm font-bold text-white">{m.pct}%</span>
                  </button>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-gradient-to-br from-zinc-900/90 to-black/90 backdrop-blur-xl border border-white/10 shadow-[0_0_35px_-10px_rgba(217,70,239,0.3)] rounded-2xl p-6 space-y-5"
            >
              <h3 className="text-lg font-bold text-white">{language === 'en' ? 'Your metabolism' : 'Votre métabolisme'}</h3>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-zinc-400">{language === 'en' ? 'Basal metabolic rate (BMR)' : 'Métabolisme de base (BMR)'}</span>
                <span className="text-white font-bold">{Math.round(results.bmr)} kcal</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-zinc-400">{language === 'en' ? 'Total expenditure (TDEE)' : 'Dépense totale (TDEE)'}</span>
                <span className="text-white font-bold">{Math.round(results.tdee)} kcal</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-zinc-400">{language === 'en' ? 'Morphotype' : 'Morphotype'}</span>
                <span className="text-white font-bold capitalize">{results.morphotype}</span>
              </div>
              <div className="bg-gradient-to-r from-primary/15 to-yellow-500/10 rounded-xl p-4 flex items-center gap-4 mt-2">
                {formData.goal === 'muscle_gain'
                  ? <TrendingUp className="w-8 h-8 text-yellow-400 shrink-0" />
                  : <TrendingDown className="w-8 h-8 text-primary shrink-0" />}
                <div>
                  {timeline.weeks != null ? (
                    <>
                      <p className="text-white font-bold flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> {timeline.weeks} {language === 'en' ? 'weeks' : 'semaines'} (~{timeline.months} {language === 'en' ? 'months' : 'mois'})
                      </p>
                      <p className="text-zinc-400 text-sm">
                        {Math.abs(results.weekly_change_kg)} kg / {language === 'en' ? 'week' : 'semaine'} → {timeline.target_weight} kg
                      </p>
                      {timeline.realistic === false && (
                        <p className="text-amber-300 text-xs mt-1">
                          {language === 'en' ? '⚠ This pace is ambitious — a slower rhythm is often more sustainable.' : '⚠ Ce rythme est ambitieux — un tempo plus doux est souvent plus durable.'}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-zinc-300 text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {timeline.message || (language === 'en' ? 'Set a target weight to estimate the timeline.' : 'Renseignez un poids cible pour estimer la durée.')}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* 12-week weight projection (motivational) */}
          <WeightProjection
            startWeight={Number(formData.weight)}
            weeklyChange={results.weekly_change_kg}
            targetWeight={timeline.target_weight ?? (formData.target_weight ? Number(formData.target_weight) : null)}
            goal={formData.goal}
            language={language}
          />

          {/* PRO content (after payment) */}
          {proContent && (
            <motion.section
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="mb-12"
              data-testid="pro-content-section"
            >
              <div className="flex items-center justify-end mb-4">
                <button
                  onClick={() => handlePrint(language === 'en' ? 'Pro Analysis' : 'Analyse Pro', proContent, false)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl text-sm font-semibold transition-transform hover:scale-105"
                  data-testid="download-pro-btn"
                >
                  <Download className="w-4 h-4" /> {language === 'en' ? 'Premium PDF' : 'PDF Premium'}
                </button>
              </div>
              <PaidContent content={proContent} type="pro" language={language} />
            </motion.section>
          )}

          {/* MEAL PLAN content (after payment) */}
          {mealPlanContent && (
            <motion.section
              id="meal-plan-section"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="mb-12 scroll-mt-28"
              data-testid="meal-plan-section"
            >
              <div className="flex items-center justify-end gap-2 mb-4">
                {hasCalendar && (
                  <div className="flex items-center gap-1 p-1 rounded-xl bg-zinc-900 border border-white/10 mr-auto" data-testid="meal-plan-view-toggle">
                    <button onClick={() => setMealPlanView('calendar')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${mealPlanView === 'calendar' ? 'bg-primary text-white' : 'text-zinc-400'}`}
                      data-testid="meal-plan-view-calendar-btn">
                      {language === 'en' ? 'Calendar' : 'Calendrier'}
                    </button>
                    <button onClick={() => setMealPlanView('text')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${mealPlanView === 'text' ? 'bg-primary text-white' : 'text-zinc-400'}`}
                      data-testid="meal-plan-view-text-btn">
                      {language === 'en' ? 'Text' : 'Texte'}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => handlePrint(language === 'en' ? 'Meal Plan' : 'Plan Alimentaire', mealPlanContent, true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-yellow-400 to-primary text-black rounded-xl text-sm font-semibold transition-transform hover:scale-105"
                  data-testid="download-mealplan-btn"
                >
                  <Download className="w-4 h-4" /> {language === 'en' ? 'Premium PDF' : 'PDF Premium'}
                </button>
              </div>
              {hasCalendar && mealPlanView === 'calendar' ? (
                <MealPlanCalendar calculationId={calculationId} initialDays={mealPlanStructured.days} initialShoppingList={mealPlanStructured.shopping_list} en={language === 'en'} />
              ) : (
                <PaidContent content={mealPlanContent} type="meal_plan" language={language} />
              )}
            </motion.section>
          )}

          {/* Share card — viral acquisition after unlocking premium content */}
          {(proContent || mealPlanContent) && (
            <ShareCard language={language} />
          )}

          {/* Promo code (friends / testing) */}
          {!isLoadingPro && !promoLoading && (!proContent || !mealPlanContent) && onRedeemCode && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
              className="max-w-md mx-auto mt-8 text-center"
              data-testid="promo-code-block"
            >
              <p className="text-zinc-500 text-sm mb-3">{language === 'en' ? 'Got a free code?' : 'Vous avez un code gratuit ?'}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
                  placeholder={language === 'en' ? 'Promo code' : 'Code promo'}
                  className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:border-primary focus:outline-none uppercase"
                  data-testid="promo-code-input"
                />
                <button
                  onClick={applyPromo}
                  disabled={promoLoading || !promoCode.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-xl transition-transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  data-testid="promo-code-apply-btn"
                >
                  {promoLoading ? '...' : (language === 'en' ? 'Apply' : 'Appliquer')}
                </button>
              </div>
              {promoError && <p className="text-red-400 text-sm mt-2" data-testid="promo-code-error">{promoError}</p>}
            </motion.div>
          )}

          {(isLoadingPro || promoLoading) && (
            <ProgramBuildingLoader language={language} progressStep={genStep} />
          )}
        </div>
      </main>

      {/* Coach AI gratuit : 3 questions / 24h pour booster la conversion */}
      <AICoachGuestChat
        language={language}
        calcContext={{
          goal: goalLabel,
          calories: results.target_calories,
          protein_g: macros.protein_g,
          carbs_g: macros.carbs_g,
          fat_g: macros.fat_g,
        }}
      />
    </>
  );
};

const ShareCard = ({ language }) => {
  const en = language === 'en';
  const [copied, setCopied] = useState(false);
  const shareUrl = 'https://body-up.ch/calculateur-calories';
  const shareText = en
    ? "I just got my custom nutrition plan with Body-Up 💪 Calculate yours for free:"
    : "J'ai obtenu mon programme nutrition sur-mesure avec Body-Up 💪 Calcule le tien gratuitement :";

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Body-Up', text: shareText, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) { /* ignore */ }
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
      className="relative overflow-hidden mb-12 rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/15 via-zinc-900 to-black p-6 sm:p-9"
      data-testid="share-card"
    >
      <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 border border-primary/30 mb-3">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-xs font-bold text-white tracking-wider">
              {en ? 'PROUD OF YOUR PLAN?' : 'FIER DE VOTRE PROGRAMME ?'}
            </span>
          </div>
          <h3 className="calc-display text-2xl sm:text-3xl font-black text-white leading-tight">
            {en ? 'Share it & motivate a friend' : 'Partagez-le & motivez un proche'}
          </h3>
          <p className="text-zinc-400 text-sm mt-2 max-w-md">
            {en
              ? 'Send your friends to the free calculator — they get their numbers, you stay accountable together.'
              : 'Invitez vos proches sur le calculateur gratuit — ils obtiennent leurs chiffres, et vous progressez ensemble.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-3 sm:flex-col sm:w-56">
          <button
            onClick={nativeShare}
            className="flex-1 sm:w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary hover:scale-[1.02] transition-transform"
            data-testid="share-native-btn"
          >
            <Share2 className="w-4 h-4" /> {en ? 'Share' : 'Partager'}
          </button>
          <div className="flex gap-3 flex-1 sm:w-full">
            <a
              href={waUrl} target="_blank" rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-3 rounded-xl font-semibold text-white bg-green-600/90 hover:bg-green-600 transition-colors text-sm"
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a
              href={fbUrl} target="_blank" rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-3 rounded-xl font-semibold text-white bg-blue-600/90 hover:bg-blue-600 transition-colors text-sm"
              data-testid="share-facebook-btn"
            >
              <Facebook className="w-4 h-4" /> Facebook
            </a>
          </div>
          <button
            onClick={copyLink}
            className="flex-1 sm:w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-white bg-white/5 border border-white/15 hover:bg-white/10 transition-colors text-sm"
            data-testid="share-copy-btn"
          >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            {copied ? (en ? 'Copied!' : 'Copié !') : (en ? 'Copy link' : 'Copier le lien')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ResultsView;
